import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useSyncExternalStore,
} from "react";

export type MfaRecoveryHoldSource = "passkey" | "totp";

export type MfaRecoveryHoldSnapshot = { passkey: boolean; totp: boolean };

export function createMfaRecoveryHoldStore() {
  let snapshot: MfaRecoveryHoldSnapshot = { passkey: false, totp: false };
  const listeners = new Set<() => void>();
  return {
    subscribe(onStoreChange: () => void) {
      listeners.add(onStoreChange);
      return () => {
        listeners.delete(onStoreChange);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    set(source: MfaRecoveryHoldSource, open: boolean) {
      if (snapshot[source] === open) return;
      snapshot = { ...snapshot, [source]: open };
      listeners.forEach((listener) => listener());
    },
  };
}

export type MfaRecoveryHoldStore = ReturnType<typeof createMfaRecoveryHoldStore>;

export const MfaRecoveryHoldContext = createContext<
  ((source: MfaRecoveryHoldSource, open: boolean) => void) | null
>(null);

export function useMfaRecoveryHold(source: MfaRecoveryHoldSource) {
  const report = useContext(MfaRecoveryHoldContext);
  return useCallback(
    (open: boolean) => {
      report?.(source, open);
    },
    [report, source],
  );
}

export function useMfaRecoveryHoldStore(store: MfaRecoveryHoldStore) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

export function createPasskeysSectionPresenceStore() {
  let mounted = 0;
  let snapshot = false;
  const listeners = new Set<() => void>();
  const emit = () => {
    const next = mounted > 0;
    if (next === snapshot) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    subscribe(onStoreChange: () => void) {
      listeners.add(onStoreChange);
      return () => {
        listeners.delete(onStoreChange);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    mount() {
      mounted += 1;
      emit();
    },
    unmount() {
      mounted = Math.max(0, mounted - 1);
      emit();
    },
  };
}

export type PasskeysSectionPresenceStore = ReturnType<typeof createPasskeysSectionPresenceStore>;

export const PasskeysSectionPresenceContext = createContext<PasskeysSectionPresenceStore | null>(
  null,
);

export function useReportPasskeysSectionPresence() {
  const store = useContext(PasskeysSectionPresenceContext);
  useLayoutEffect(() => {
    if (!store) return;
    store.mount();
    return () => store.unmount();
  }, [store]);
}
